import "./__queries__/accela";
import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import { THIRD_PARTY_SYSTEM_TYPE } from "common/constants";
import { getPath } from "common/routing";
import {
  AccelaConfigurationFragment,
  useCreateAccelaConfigurationMutation,
  useGetAccelaConfigurationQuery,
  useUpdateAccelaConfigurationMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import AccelaLogo from "../../../../images/integrations/accela-logo.png";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Box, FlexColumn, FlexRow } from "../../../Common/__styles__/Layout";
import {
  SettingsForm,
  SettingsGrid,
} from "../../../Common/__styles__/Settings";
import { ButtonLink } from "../../../Common/ButtonLink";
import { Button } from "../../../Common/Buttons/Button";
import { CopyEntityID } from "../../../Common/CopyEntityID";
import FullPageLoadingScreen from "../../../Common/FullPageLoadingScreen";
import { Body, Title } from "../../../Common/Typography";
import { Label } from "../../../Inputs";
import { Password, Text } from "../../../Inputs/react-hook-form";

interface FormStructure {
  id?: string;
  name: string;
  agencyName: string;
  environment: string;
  username: string;
  password: string;
  newPassword: string;
  scope: string;
}

const SettingSection = ({ children }: { children: React.ReactNode }) => {
  return <FlexColumn style={{ width: "100%" }}>{children}</FlexColumn>;
};

interface AccelaFormProps {
  existingConfiguration: Maybe<AccelaConfigurationFragment>;
  onRefetch: () => void;
}

const AccelaForm = ({ existingConfiguration, onRefetch }: AccelaFormProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [createConfiguration, { loading: isCreating }] =
    useCreateAccelaConfigurationMutation({
      onCompleted: () => {
        addSuccessToast("Accela configuration saved successfully");
        onRefetch();
      },
      onError: () => {
        addErrorToast(
          "There was a problem saving your Accela configuration. Please try again or contact support@withforerunner.com",
        );
      },
    });

  const [updateConfiguration, { loading: isUpdating }] =
    useUpdateAccelaConfigurationMutation({
      onCompleted: () => {
        addSuccessToast("Accela configuration updated successfully");
        onRefetch();
      },
      onError: () => {
        addErrorToast(
          "There was a problem updating your Accela configuration. Please try again or contact support@withforerunner.com",
        );
      },
    });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormStructure>({
    defaultValues:
      existingConfiguration ?
        {
          id: existingConfiguration.id,
          name: existingConfiguration.name,
          agencyName: existingConfiguration.sourceConfiguration.agencyName,
          environment: existingConfiguration.sourceConfiguration.environment,
          username: existingConfiguration.sourceConfiguration.username,
          scope: existingConfiguration.sourceConfiguration.scope,
        }
      : {},
  });

  const isLoading = isCreating || isUpdating;

  const onSubmit = async (data: FormStructure) => {
    const sourceConfiguration = {
      type: THIRD_PARTY_SYSTEM_TYPE.ACCELA,
      agencyName: data.agencyName,
      environment: data.environment,
      username: data.username,
      scope: data.scope,
    };

    if (existingConfiguration) {
      await updateConfiguration({
        variables: {
          data: {
            id: existingConfiguration.id,
            type: THIRD_PARTY_SYSTEM_TYPE.ACCELA,
            name: data.name,
            sourceConfiguration: {
              ...sourceConfiguration,
              newPassword: data.newPassword,
            },
          },
        },
      });
    } else {
      await createConfiguration({
        variables: {
          data: {
            type: THIRD_PARTY_SYSTEM_TYPE.ACCELA,
            name: data.name,
            sourceConfiguration: {
              ...sourceConfiguration,
              password: data.password,
            },
          },
        },
      });
    }
  };

  return (
    <SettingsForm>
      <SettingSection>
        <SettingsGrid>
          <div>
            <Label text="Configuration name" htmlFor="name" required />
            <Text
              {...register("name", {
                required: "Configuration name is required",
              })}
              required
              error={errors.name?.message}
              size="medium"
            />
          </div>
          <div>
            <Label text="Agency name" htmlFor="agencyName" required />
            <Text
              {...register("agencyName", {
                required: "Agency name is required",
              })}
              required
              error={errors.agencyName?.message}
              size="medium"
            />
          </div>
          <div>
            <Label text="Username" htmlFor="username" required />
            <Text
              {...register("username", {
                required: "Username is required",
              })}
              required
              error={errors.username?.message}
              size="medium"
            />
          </div>
          {existingConfiguration && (
            <div>
              <Label text="New Password" htmlFor="newPassword" />
              <Password
                {...register("newPassword")}
                required
                error={errors.newPassword?.message}
                size="medium"
                placeholder="Enter new password, or leave blank to keep current password"
              />
            </div>
          )}

          {!existingConfiguration && (
            <div>
              <Label text="Password" htmlFor="password" required />
              <Password
                {...register("password", {
                  required: "Password is required",
                })}
                required
                error={errors.password?.message}
                size="medium"
              />
            </div>
          )}

          <div>
            <Label text="Environment" htmlFor="environment" required />
            <Text
              {...register("environment", {
                required: "Environment is required",
              })}
              required
              error={errors.environment?.message}
              size="medium"
            />
          </div>
          <div>
            <Label text="Scope" htmlFor="scope" required />
            <Text
              {...register("scope", { required: "Scope is required" })}
              required
              error={errors.scope?.message}
              size="medium"
            />
          </div>
        </SettingsGrid>
      </SettingSection>

      <Button
        variant="primary"
        size="medium"
        onClick={handleSubmit(onSubmit)}
        title={
          existingConfiguration ?
            "Update configuration"
          : "Create configuration"
        }
        isDisabled={isLoading}
      />
    </SettingsForm>
  );
};

export const Accela = () => {
  const { account } = useContext(AuthContext);

  const {
    data: configurationData,
    loading: configurationLoading,
    refetch: refetchConfiguration,
  } = useGetAccelaConfigurationQuery({
    variables: { accountId: account?.id! },
    skip: !account?.id,
  });

  if (!account) {
    return null;
  }

  if (configurationLoading) {
    return <FullPageLoadingScreen />;
  }

  const existingConfiguration =
    configurationData?.account?.thirdPartySystemConfigurations[0] ?? null;

  return (
    <Box>
      <FlexColumn gap="l" style={{ padding: spacing.l.value }}>
        <ButtonLink
          styleVariant="primary"
          size="small"
          leftIconName="chevron-left"
          to={getPath("integrations")}
          style={{ width: "fit-content" }}
        >
          Back
        </ButtonLink>
        <FlexRow style={{ justifyContent: "space-between" }}>
          <FlexRow gap="m" style={{ alignItems: "center" }}>
            <img src={AccelaLogo} alt="Accela" width={40} height={40} />
            <FlexColumn>
              <FlexRow gap="xs" style={{ alignItems: "center" }}>
                <Title size="small" type="semiBold">
                  Accela
                </Title>
                <CopyEntityID
                  entityId={existingConfiguration?.id!}
                  tooltipText="Copy Accela configuration ID"
                />
              </FlexRow>
              <Body size="default" type="regular" color="contentSecondary">
                Share data between Forerunner and Accela to streamline your
                workflows
              </Body>
            </FlexColumn>
          </FlexRow>
        </FlexRow>

        <AccelaForm
          existingConfiguration={existingConfiguration}
          onRefetch={refetchConfiguration}
        />
      </FlexColumn>
    </Box>
  );
};
